// crewbit-admin/src/hooks/usePaymentLedger.ts
//
// Hook contract: docs/references/MONEY_MODULE_SPEC_v1.md §InvoiceDetail.6 (B-N10).
// Read-only payment ledger rows for a single invoice. Reads from payment_ledger
// filtered by invoice_id. Includes refunds (direction='out') alongside payments
// (direction='in') for the ledger total math. Mirrors mobile.
//
// Web runtime: Page Visibility API for focus refetch; window event for
// MONEY_MUTATION_EVENT (no DeviceEventEmitter equivalent).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { MONEY_MUTATION_EVENT } from './useFinancialSummary';

const FOCUS_REFETCH_THROTTLE_MS = 30_000;

export interface PaymentLedgerRow {
  id: string;
  business_id: string;
  direction: 'in' | 'out';
  entity_type: string;            // 'client_invoice' for invoice rows
  entity_id: string;
  invoice_id: string | null;
  amount: number;
  payment_method: string;
  description: string | null;
  date: string;
  created_at: string;
  tip_amount: number | null;
  hst_amount: number | null;
  client_name: string | null;
  stripe_event_id: string | null;
  stripe_session_id: string | null;
  stripe_refund_id: string | null;
  attributed_to_user_id: string | null;
  attribution_role: string | null;
}

export interface UsePaymentLedgerArgs {
  invoiceId: string | null;
}

export interface UsePaymentLedgerReturn {
  data: PaymentLedgerRow[] | null;
  isLoading: boolean;
  isRefreshing: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

const SELECT_COLS =
  'id, business_id, direction, entity_type, entity_id, invoice_id, amount, ' +
  'payment_method, description, date, created_at, tip_amount, hst_amount, ' +
  'client_name, stripe_event_id, stripe_session_id, stripe_refund_id, ' +
  'attributed_to_user_id, attribution_role';

export function usePaymentLedger({ invoiceId }: UsePaymentLedgerArgs): UsePaymentLedgerReturn {
  const [data, setData] = useState<PaymentLedgerRow[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const dataRef = useRef<PaymentLedgerRow[] | null>(null);
  useEffect(() => { dataRef.current = data; }, [data]);

  const lastFocusFetchRef = useRef<number>(0);
  const inFlightRef = useRef<boolean>(false);
  const mountedRef = useRef<boolean>(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const doFetch = useCallback(
    async (manual: boolean) => {
      if (!invoiceId) return;
      if (inFlightRef.current) return;
      inFlightRef.current = true;

      const isInitial = dataRef.current === null;
      if (isInitial) setIsLoading(true);
      else if (manual) setIsRefreshing(true);

      try {
        const { data: rows, error: queryError } = await supabase
          .from('payment_ledger')
          .select(SELECT_COLS)
          .eq('invoice_id', invoiceId)
          .order('created_at', { ascending: false });

        if (!mountedRef.current) return;
        if (queryError) {
          setError(new Error(queryError.message));
        } else {
          setData((rows ?? []) as unknown as PaymentLedgerRow[]);
          setError(null);
        }
      } finally {
        if (mountedRef.current) {
          setIsLoading(false);
          setIsRefreshing(false);
        }
        inFlightRef.current = false;
      }
    },
    [invoiceId],
  );

  // Reset on invoiceId change.
  useEffect(() => {
    setData(null);
    setError(null);
  }, [invoiceId]);

  // Mount fetch.
  useEffect(() => {
    if (!invoiceId) return;
    void doFetch(false);
  }, [invoiceId, doFetch]);

  // Page Visibility focus refetch.
  useEffect(() => {
    if (!invoiceId) return;
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastFocusFetchRef.current >= FOCUS_REFETCH_THROTTLE_MS) {
        lastFocusFetchRef.current = now;
        void doFetch(true);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => { document.removeEventListener('visibilitychange', onVisibility); };
  }, [invoiceId, doFetch]);

  // MONEY_MUTATION_EVENT cache invalidation.
  useEffect(() => {
    const onMutation = () => { if (invoiceId) void doFetch(true); };
    window.addEventListener(MONEY_MUTATION_EVENT, onMutation);
    return () => { window.removeEventListener(MONEY_MUTATION_EVENT, onMutation); };
  }, [invoiceId, doFetch]);

  const refetch = useCallback(async () => { await doFetch(true); }, [doFetch]);

  return useMemo(
    () => ({ data, isLoading, isRefreshing, error, refetch }),
    [data, isLoading, isRefreshing, error, refetch],
  );
}
